import { TButtonTheme } from '@cian/ui-kit/button/types';
import { useToast } from '@cian/ui-kit/toast';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CompareButtonControl } from './CompareButtonControl';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackRemoveNewbuildingFromComparison,
  type TComparisonEventSource,
} from './tracking';
import { requestAuthentication } from '../../actions/authentication';
import { setNewbuildingComparisonStatus } from '../../actions/newbuildingComparison';
import { useApplicationContext } from '../../containers/ApplicationContext';
import { selectIsAuthenticated } from '../../selectors';
import { getNewbuildingId, getOffer } from '../../selectors/newObject';
import { getAgent } from '../../selectors/newObject/getAgent';
import { getNewbuildingComparisonStatus } from '../../selectors/newbuildingComparison';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { DispatchReduxAction, IThunkActionCreator } from '../../store';
import { getCustomUrl } from '../../utils/tracking/getCustomUrl';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingCompareButtonProps {
  buttonTheme?: TButtonTheme;
  source: TComparisonEventSource;
}

export const NewbuildingCompareButton: React.FC<INewbuildingCompareButtonProps> = ({ buttonTheme, source }) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch: DispatchReduxAction = useDispatch();
  const comparisonStatus = useSelector(getNewbuildingComparisonStatus);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const newbuildingId = useSelector(getNewbuildingId);
  const offer = useSelector(getOffer);
  const agent = useSelector(getAgent);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isAdded = comparisonStatus === 'added';
  const isComparisonAvailable = comparisonStatus === 'added' || comparisonStatus === 'available';

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult, comparisonAction?: 'add' | 'remove') => {
      const onComparisonClick =
        comparisonAction === 'add' ? trackClickAddComparisonPopup : trackClickRemoveComparisonPopup;

      toast.open({
        type: result.success === false ? 'error' : 'success',
        hasCloseIcon: false,
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl}
            onClick={() => onComparisonClick(newbuildingId as number)}
          />
        ),
      });
    },
    [toast, newbuildingId],
  );

  const toggleComparison = React.useCallback(async () => {
    if (!newbuildingId) {
      return;
    }

    const previousStatus = comparisonStatus;
    const nextStatus = isAdded ? 'available' : 'added';

    dispatch(setNewbuildingComparisonStatus(nextStatus));

    try {
      const result = !isAdded
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      if (result.success === false) {
        showComparisonToast(result);
        dispatch(setNewbuildingComparisonStatus(previousStatus));

        return;
      }

      showComparisonToast(result, isAdded ? 'remove' : 'add');

      const gaLabel = getCustomUrl(offer, agent);

      if (!isAdded) {
        trackAddNewbuildingToComparison(newbuildingId, gaLabel, source);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId, gaLabel, source);
      }
    } catch (_error) {
      dispatch(setNewbuildingComparisonStatus(previousStatus));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [comparisonStatus, dispatch, httpApi, isAdded, logger, newbuildingId, offer, agent, showComparisonToast, source]);

  const handleClick = React.useCallback(() => {
    if (!isAuthenticated) {
      dispatch(
        requestAuthentication({
          source: 'compareNewbuildings',
          onSuccess: (): IThunkActionCreator => () => {
            void toggleComparison();
          },
        }),
      );

      return;
    }

    void toggleComparison();
  }, [dispatch, isAuthenticated, toggleComparison]);

  if (!isCompareEnabled || !newbuildingId || !isComparisonAvailable) {
    return null;
  }

  return <CompareButtonControl buttonTheme={buttonTheme} isAdded={isAdded} onClick={handleClick} />;
};
